import Handlebars from "handlebars/dist/handlebars.min.js";
import CustomizableInterface from "./interfaces/CustomizableInterface";
import NavigationController from "./controller/NavigationController";

import InterfaceTransitions from "./interfaces/Transition";
import Sidemenu from "./panels/Sidemenu";
import StoryMode from "./interfaces/StoryMode";

import Utils from "./utils/Utils";
import Swiper from "./Swiper";
import NotificationBar from "./utils/NotificationBar";
import Renderer from "./Renderer";

/**
 * @creator Stefano
 * @date 26-6-2018
 */
export default class InterfaceController {
    constructor(fc) {
        this.dataHelper = fc.dataHelper;
        this.fc = fc;
        this.InterfaceTransitions = null;
        this._stickyget = {};
        this._spread_stop = [];
        this._scrollingList = {};
        this._inTransition = false;
        this._toFastIndex = null;
        this.sidemenu = {};
        this.bindSideButton();
        this._bindBackButton();
        this.contentSlider = document.getElementById("content-slider");

        Handlebars.registerHelper("spread_index", function (value) {
            return parseInt(value) + 1;
        });

        this._classInterface = new CustomizableInterface(this);

        if (document.querySelector(".viewer.storyMode")) {
            this.storyMode = new StoryMode();
            this.storyMode.on("GoToSpread", (eventData) => {
                this.fc.gotoSpread(eventData.spreadId);
            });
            this.storyMode.on("StoryTransitionDone", (eventData) => {
                this._inTransition = false;
                // this.fc.setSpreadStatus(eventData.spreadId, this.fc.SPREADSTATUSLIST.READYTOSTART);
                if (this._toFastIndex) {
                    this.storyMode._resetMainStage();
                    this.storyMode._moveSpreadToMainStage(this._toFastIndex.spread_id, this._toFastIndex.classname);
                    this.fc.transitionDone(this._toFastIndex.spread_id);
                    this._toFastIndex = null;
                } else {
                    this.fc.transitionDone(eventData.spreadId);
                }
            });
            this.storyMode.on("StoryTransitionOutDone", (eventData) => {
                this.fc.executeStopSpread(eventData.spreadId);
            });
            this.storyMode.on("PageReadyToStart", (eventData) => {
                this.fc.setSpreadStatus(eventData.spreadId, this.fc.SPREADSTATUSLIST.READYTOSTART);
                this.fc.transitionDone(eventData.spreadId);
            });
        } else {
            this.setTransitionSlider();
        }

        this.sidemenu = new Sidemenu(this.dataHelper);
        this.sidemenu.on("MENUTOGGLE", (event) => {
            let spread_id = this.fc.getCurrentSpreadId();
            if (spread_id) {
                if (event.action === "OPEN") {
                    this.shiftScrollToSpread(spread_id);
                    this.updateBodyScrollLock(false);
                    this._classInterface.setTemplateMode(false);
                } else {
                    this.shiftScrollFromSpread(spread_id);
                    this.fc.getSpreadController(spread_id).startSpread(spread_id);
                    this.updateBodyScrollLock(true);
                    this._classInterface.restoreTemplateMode();
                }
            }
        });

        this.bindNavigation();

        this.interfaceElement = document.querySelector(".interface");

        this.bindCategoryButtons();

        if (window.interface_settings?.settings?.autoplay) {
            this.setupAutoplay(window.interface_settings?.settings?.autoplay);
        }
        if (this.contentSlider) {
            this.swiper = new Swiper(this.contentSlider);
            this.swiper.bind();
            this.swiper.on("SWIPED", (event) => {
                if (event.left) {
                    if (!this.storyMode) this.fc.nextSpread();
                } else if (event.right) {
                    if (!this.storyMode) this.fc.previousSpread();
                } else if (event.top) {
                    // next Scene
                    let spread_id = this.fc.getCurrentSpreadId();
                    this.fc.getSpreadController(spread_id)?.sceneLink(spread_id, "NEXT");
                } else if (event.bottom) {
                    // previous Scene
                    let spread_id = this.fc.getCurrentSpreadId();
                    this.fc.getSpreadController(spread_id)?.sceneLink(spread_id, "PREVIOUS");
                }
            });
        }

        document.querySelectorAll("a.skiplink").forEach((entry) =>
            entry.addEventListener("keyup", (e) => {
                if (e.key === "Enter") {
                    if (e.currentTarget.dataset.skiptotarget === "current-spread") {
                        document
                            .querySelector(`[data-spread_id="${this.fc.getCurrentSpreadId()}"]`)
                            .focus({ preventScroll: true });
                    } else {
                        switch (document.querySelector(".overview-content").dataset.overviewcontent) {
                            case "recent":
                                document
                                    .getElementById("recent-issues-list")
                                    .querySelector(".issue-item")
                                    .focus({ preventScroll: true });
                                break;
                            case "legacy":
                                document
                                    .getElementById("overview-legacy-issue-list")
                                    .querySelector(".issue-item")
                                    .focus({ preventScroll: true });
                                break;
                            case "category":
                                document
                                    .getElementById("overview-issue-list")
                                    .querySelector(".issue-item")
                                    .focus({ preventScroll: true });
                                break;
                        }
                    }
                }
            }),
        );

        Renderer.registerPartial("issue_cover", document.getElementById("HB-issue-cover")?.innerHTML || "");
    }

    urlChanged() {
        let spread_id = this.fc._spread_id;
        let index = this.fc.spreadOrder.indexOf(spread_id);
        if (this.sidemenu.closeMenu) {
            this.sidemenu.closeMenu(500);
        }

        if (this.storyMode) {
            this.doStoryModeNavigation(spread_id);
        } else {
            if (this.fc.getCurrentPublicationType() !== 4) {
                if (index > -1 && this?.InterfaceTransitions.getCurrent() !== index) {
                    this.moveSliderToSpread(spread_id);
                } else {
                    // _toFastIndex holds a spread id (resetPage matches it against data-article-id)
                    this.InterfaceTransitions._toFastIndex = index > -1 ? spread_id : -1;
                    // this.fc.startEvents();
                    this.fc.startSpread();
                }
            }
        }
    }

    doStoryModeNavigation(spread_id) {
        let {
            action,
            classname,
            offset,
            previousSpreadId,
            backgroundSpreadId,
            stackSize,
            direction,
            multipop,
            preloaded,
        } = this.storyMode.resolveAction(spread_id);

        if (this._inTransition && this.storyMode.getCarouselMode()) {
            if (classname) {
                this._toFastIndex = { spread_id: spread_id, classname: classname };
                Utils.simpleDebounce(this.storyMode.setCarouselPointer, 100, this.storyMode)(spread_id);
            }
            return;
        }

        if (previousSpreadId !== spread_id) {
            let startDelay = 0;
            if (!preloaded && (previousSpreadId || backgroundSpreadId)) {
                switch (action) {
                    case "pop":
                        if (previousSpreadId) {
                            this.fc.getSpreadController(previousSpreadId)?.reportScrollDepth?.(previousSpreadId);
                            this.shiftScrollToSpread(previousSpreadId, spread_id);
                            this.removeSpreadScrollListing(previousSpreadId);
                            this.fc.executeStopSpread(previousSpreadId);
                            this.storyMode.moveFromMainStageToBackStage(previousSpreadId);
                        }

                        //put the new background in the background
                        if (backgroundSpreadId) {
                            this.storyMode.moveFromBackStageToBackground(backgroundSpreadId);
                            this.storyMode.setBackgroundClass(classname);
                        }

                        this.storyMode.moveFromBackgroundToMainStage(spread_id, classname);
                        startDelay = 600;
                        if (multipop) {
                            multipop.forEach((spread_id) => {
                                this.fc.executeStopSpread(spread_id);
                            });
                        }
                        break;
                    case "push": {
                        this.fc.getSpreadController(backgroundSpreadId)?.reportScrollDepth?.(backgroundSpreadId);
                        this.storyMode.setBackgroundClass("add-level");
                        let backgroundClassname = false;
                        if (classname === "story-offset-left") {
                            backgroundClassname = "mainstage-to-background";
                        }
                        this.shiftScrollToSpread(backgroundSpreadId, spread_id);
                        this.fc.pauseSpread(backgroundSpreadId);

                        // if(!previousSpreadId){
                        //     this.storyMode.moveFromBackStageToBackground( backgroundSpreadId , previousSpreadId);
                        // }else{
                        this.storyMode.moveFromMainStageToBackground(
                            backgroundSpreadId,
                            previousSpreadId,
                            backgroundClassname,
                        );
                        // }

                        if (stackSize > 1) {
                            this._inTransition = true;
                        }
                        this.storyMode.moveFromBackstageToMainStage(spread_id, classname);
                        break;
                    }
                    case "replace":
                        this.fc.getSpreadController(previousSpreadId)?.reportScrollDepth?.(previousSpreadId);
                        this.shiftScrollToSpread(previousSpreadId, spread_id);
                        this.fc.pauseSpread(previousSpreadId);
                        if (direction) {
                            this.contentSlider.setAttribute("data-popup-transition", direction);
                            this._inTransition = true;
                            this.storyMode.moveFromBackstageToMainStage(spread_id, classname);
                        } else {
                            this.contentSlider.setAttribute("data-sibling-transition", "crossfade");
                            this._inTransition = true;
                            this.storyMode.once("StoryTransitionDone", (eventData) => {
                                if (eventData.spreadId === spread_id) {
                                    this.storyMode.moveFromMainStageToBackStage(previousSpreadId);
                                    this.contentSlider.removeAttribute("data-sibling-transition");
                                }
                            });
                            this.storyMode.moveFromBackstageToMainStage(spread_id, classname);
                        }

                        break;
                }
            } else {
                // this can only occur when page is refreshed.
                document.getElementById(`maglr-article-${spread_id}`).classList.add(classname);
                this.storyMode.setControlsClass(classname);

                //put the new background in the background
                if (backgroundSpreadId) {
                    this.storyMode.moveFromBackStageToBackground(backgroundSpreadId);
                    this.storyMode.setBackgroundClass(classname);
                }
            }

            this.shiftScrollFromSpread(spread_id);
            if (startDelay > 0) {
                setTimeout(() => {
                    this.fc.startSpread(offset);
                }, startDelay);
            } else {
                this.fc.startSpread(offset);
            }
        }
    }

    moveSliderToSpread(spread_id) {
        this.fc.moveSpreadToMainStage(spread_id);

        //save previous scroll offset for old spread
        if (this.fc._previousSpreadId && this.fc._previousSpreadId !== spread_id) {
            this.fc.getSpreadController(this.fc._previousSpreadId)?.reportScrollDepth?.(this.fc._previousSpreadId);
            this.shiftScrollToSpread(this.fc._previousSpreadId, spread_id);
        }

        let index = this.fc.spreadOrder.indexOf(spread_id);
        this.InterfaceTransitions.goToPage(this.animationDone, index, spread_id, this.fc._previousSpreadId, this);
    }

    setTransitionSlider() {
        this.InterfaceTransitions = new InterfaceTransitions(this.fc);
        this.InterfaceTransitions.init(this.contentSlider);
    }

    bindNavigation() {
        $(".interface-next").click(() => {
            this.fc.nextSpread();
        });
        $(".interface-previous").click(() => {
            this.fc.previousSpread();
        });
    }

    /**
     *
     *
     * @private
     */
    renderHomepage(filter) {
        //check if we already have rendered the current filter
        if (this._iscached_filter === filter) {
            return false;
        }

        //if home is disabled we do not have to to anything
        const overviewContent = document.querySelector(".overview-content");
        if (!overviewContent) {
            return false;
        }

        this._iscached_filter = filter;
        let category_id;
        let category;

        let raw_issues, contenttype;
        if (
            Object.hasOwn(this._stickyget, "recent") ||
            (filter === false && window.interface_settings.settings?.navigation.overview_type === 3)
        ) {
            raw_issues = this.dataHelper.filterRecentIssues();
            contenttype = "recent";
            category_id = "recent";
        } else if (Object.hasOwn(this._stickyget, "legacy")) {
            contenttype = "legacy";
            category_id = "legacy";
        } else {
            if (isNaN(filter) && filter !== undefined) {
                category = this.dataHelper.getCategoryByName(filter);
                category_id = category && category.category_id;
            } else if (!filter) {
                category = this.dataHelper.getFirstCategory();
                category_id = category && category.category_id;
            } else {
                category_id = filter;
            }

            raw_issues = this.dataHelper.filterIssues(category_id, 1); //remove any inactive spreads + order them in the correct way
            contenttype = "category";
        }

        overviewContent.setAttribute("data-overviewcontent", contenttype);
        this.handleCategorySwitch(category_id ?? this.dataHelper._getDefaultCategory());
        if (raw_issues) {
            const issues = this.dataHelper.prepareIssuesForRender(raw_issues);

            if (issues.length !== 0) {
                if (contenttype !== "recent") {
                    this._classInterface.renderHomepage(issues);
                } else {
                    this._classInterface.renderRecent(issues);
                }

                category && this._classInterface.setOverviewTitle(category.name);

                this._classInterface.updateBreadCrumb(this.dataHelper.getBreadCrumb(false, false));
            }

            this.bindHistoryPushLinks();
        }

        window.previewMode &&
            NotificationBar.showNotification(
                window.interface_settings?.settings?.text?.warning_preview_overview,
                "notice",
            );

        if (typeof window.CustomEvent === "function" && typeof document.dispatchEvent === "function") {
            // too bad for you IE
            document.dispatchEvent(new CustomEvent("HOMEPAGE_RENDERED"));
        }
    }

    bindHistoryPushLinks() {
        $("[data-historypush]")
            .off("click.default")
            .on("click.default", (event) => {
                if (this.fc.getCurrentPublicationType(event.currentTarget?.dataset?.issueId) !== 4) {
                    event.preventDefault();
                    event.stopPropagation();
                    this.fc._homepage = false; //TODO: is dit nodig?

                    // var el = document.querySelector('.basic-go-up');
                    // var par = el.parentNode;
                    // var next = el.nextSibling;
                    // par.removeChild(el);
                    // setTimeout(function() {par.insertBefore(el, next);}, 100);

                    if (Utils.hasHashBinding()) {
                        event.currentTarget?.dataset?.spreadlink &&
                            this.fc.gotoSpread(parseInt(event.currentTarget?.dataset?.spreadlink, 10));
                    } else {
                        NavigationController.pushState({}, "", $(event.currentTarget).attr("href"));
                    }
                }
            });
    }

    bindWindowResize() {
        if (!this._windowResizeHandler) {
            this._windowResizeHandler = Utils.debounce(
                () => {
                    this.scaleRatio();
                },
                500,
                false,
            );
            window.addEventListener("resize", this._windowResizeHandler);
        }
    }

    /**
     *
     * @param category_id
     * @public
     */
    handleCategorySwitch(category_id) {
        this._classInterface.setCategoryActive(category_id);
    }

    bindCategoryButtons() {
        $("a[data-cat-name]").on("click", (event) => {
            event.preventDefault();
            event.stopPropagation();

            //if we have a category set then call url delegate to render this page
            let title =
                window.interface_settings.settings?.text?.magazine_environment_name ||
                window.interface_settings.settings?.text?.overview_title ||
                "";

            switch (event.currentTarget.dataset.cat_id) {
                case "recent":
                    delete this._stickyget["category"];
                    delete this._stickyget["legacy"];
                    this._stickyget["recent"] = "recent";
                    break;
                case "legacy":
                    delete this._stickyget["category"];
                    delete this._stickyget["recent"];
                    this._stickyget["legacy"] = "legacy";
                    break;
                default: {
                    let categoryName = event.currentTarget.getAttribute("title");
                    let categorySystemName = event.currentTarget.dataset?.catName;
                    delete this._stickyget["recent"];
                    delete this._stickyget["legacy"];

                    if (categoryName) {
                        this._stickyget["category"] = categorySystemName;

                        title = `${title} - ${categoryName}`;
                    } else {
                        //render without a category filter
                        delete this._stickyget["category"];
                    }
                    break;
                }
            }

            NavigationController.pushState({}, title, `${Utils.createGetString(this._stickyget)}`);

            try {
                document.addEventListener(
                    "HOMEPAGE_RENDERED",
                    () => {
                        document.getElementById("overview-issue-list").querySelector("a").focus();
                    },
                    { once: true },
                );
            } catch (e) {
                console.log("no options support", e);
            }
        });
    }

    showViewerDirect() {
        this._classInterface.showViewerDirect(); //open the viewer directly
    }

    getInterfaceInstance() {
        return this._classInterface;
    }

    bindSideButton() {
        $(".interface-open-menu, .icon-menu").on("click", () => {
            $("#navigation-container").fadeToggle();
        });

        $(".close-menu, .overlay-menu").on("click", () => {
            this.hideSpreadMenu();
        });

        $(".menu-down").on("click", function (e) {
            e.stopPropagation();
            e.stopImmediatePropagation();
        });

        $('[data-action="pdf-download"]').on("click", (e) => {
            let url = `/s/pdf/create/${this.fc._issue_id}/${self.client_id}`;
            if ($(e.currentTarget).hasClass("single")) {
                url += `/${window.frontend.getCurrentSpreadId()}`;
            }
            this.generatePDF(url);
        });
    }

    generatePDF(url) {
        Utils.AjaxPromise({ url: url })
            .then(
                (response) => {
                    //this.spreads = new Map([data.spreads]);
                    let lang =
                        $('[http-equiv="content-language"]')?.attr("content")?.toLowerCase() || window.frontend._LANG;
                    window.open(`/s/pdf/issue/${response.token}?set_lang=${lang}`, "_blank");
                },
                () => {
                    console.error("MAGLR ERROR: Ajax error pdfCreate");
                },
            )
            .catch((error) => {
                console.error("MAGLR ERROR: [createPDF]", error);
            });
    }

    /**
     * Add or remove classes on the interfaceContainer based on the given spreadIndex and lastIndex. Based on the classes the next and/or previous buttons
     * will be shown/hidden.
     *
     * @param {number} spreadIndex
     * @param {number} lastIndex
     */
    disableButtons(spreadIndex, lastIndex) {
        if (this.interfaceElement) {
            if (spreadIndex === 0) {
                this.interfaceElement.classList.add("first_spread");
            } else {
                this.interfaceElement.classList.remove("first_spread");
            }

            if (spreadIndex === lastIndex) {
                this.interfaceElement.classList.add("last_spread");
            } else {
                this.interfaceElement.classList.remove("last_spread");
            }
        }

        this.swiper && this.swiper.disableEffect(spreadIndex === 0, spreadIndex === lastIndex);
    }

    animationDone() {
        this.contentSlider.classList.remove("spreadInTransition");
        this.shiftScrollFromSpread(this.fc._spread_id);
        if (this._spread_stop.length > 0) {
            for (let i in this._spread_stop) {
                if (this._spread_stop[i] !== this.fc._spread_id) {
                    this.fc.executeStopSpread(this._spread_stop[i]);
                }
            }
            this._spread_stop = [];
        }

        // this.fc.startEvents();
        // Work deferred mid-transition (initSpread left the spread at WAITINGTOCREATE, or a
        // consumed PAGE_READY left it at CREATED) must be resumed now; storyMode resumes it
        // via StoryTransitionDone, the slider has to do it here.
        this.fc.transitionDone(this.fc._spread_id);

        Utils.setLocationHash();
    }

    handleStopSpread(spread_id) {
        this._spread_stop.push(spread_id);
    }

    /**
     *  Set the new page number, also load the next lying iframes.
     *  @param element
     *  @param object
     */
    setPageNumber() {
        const spreadId = this.fc.getCurrentSpreadId();
        const issueId = this.fc.getCurrentIssueId();
        if (isNaN(spreadId) || isNaN(issueId)) return;

        this.controlsCheck(spreadId, this.fc.spreadOrder);

        const spread = this.dataHelper.getSpreadById(issueId, spreadId);
        this._classInterface.updateActiveSpread(spread, this.dataHelper.getMainSpreadsCount(issueId));
        this._classInterface.updateBreadCrumb(this.dataHelper.getBreadCrumb(issueId, spreadId));

        const closeButton = this.dataHelper.getSubCloseButtonEnabled(issueId, spreadId);
        if (closeButton === "close") {
            this.hideSubCloseButton();
        } else if (closeButton) {
            this.showSubCloseButton();
        }
    }

    showSubCloseButton() {
        $(".go-level-up-holder")
            .addClass("visible")
            .off("click")
            .one("click", () => {
                this.closeLevel();
            });
    }

    hideSubCloseButton() {
        $(".go-level-up-holder").removeClass("visible");
    }

    closeLevel() {
        if (this.storyMode) {
            this.storyMode.closeLevel();
        } else {
            const spreadId = this.fc.getCurrentSpreadId();
            const issueId = this.fc.getCurrentIssueId();
            const spread = this.dataHelper.getSpreadById(
                issueId,
                this.dataHelper.findMainSpreadIdOfSubSpread(issueId, spreadId),
            );
            this.fc.gotoSpread(spread.spread_id);
        }
    }

    reflow() {
        this.bindWindowResize();
        this.scaleRatio();
    }

    /**
     * same as _handleNavigationButton
     * @param spread_id
     * @param spreadList
     */
    controlsCheck(spread_id, spreadList) {
        let spread_index = spreadList.indexOf(spread_id);
        this.disableButtons(spread_index, spreadList.length - 1);

        if (Utils.isDownloaded()) {
            // this.setCurrentIndex( spread_index );
            this.setActive(spread_id);
        }
    }

    _navigateFirstSpread() {
        let spread_id = this.dataHelper.getFirstSpreadId(this.fc._issue_id);
        let spread = this.dataHelper.getSpreadById(this.fc._issue_id, spread_id);
        let spread_name = Utils.urlEncodeString(spread["spread_title"]); //replace the %20 with a underscore
        let request = location.pathname.split("/").splice(1);
        let url_abbr = request[0];

        let title = self?.interface_settings?.settings?.text?.magazine_environment_name || document.title;

        if (Utils.isEmpty(spread.url)) {
            NavigationController.pushState(
                {},
                title,
                "/" + url_abbr + "/" + this.fc._issue_id + "/" + spread_id + "/" + spread_name + ".html",
            );
        } else {
            NavigationController.pushState({}, title, "/" + spread.url);
        }
    }

    navigateBack(target) {
        let title = self?.interface_settings?.settings?.text?.magazine_environment_name || document.title;

        //if we have first-spread we don't have a homepage to back so we go back to first spread instead
        if ((target && $(target).hasClass("first-spread")) || !self.interface_settings?.settings?.overview_enabled) {
            this._navigateFirstSpread();
        } else {
            if (this.storyMode) {
                window.location.href = "/";
            } else {
                this.fc.IssueAudio.stopSpreadAudio();
                this.fc._homepage = true;
                NavigationController.pushState({}, title, `/${Utils.createGetString(this._stickyget)}`);
            }
        }
    }

    setStickyGet(get) {
        this._stickyget = get;
    }

    /**
     * new
     * @param index
     */
    setCurrentIndex(index) {
        this.InterfaceTransitions && this.InterfaceTransitions.setCurrent(index);
    }

    setActive() {}

    hideSpreadMenu() {
        $("#navigation-container").hide();
    }

    showViewer(switchIssue = false) {
        this._classInterface.showViewer(switchIssue);
        this.sidemenu.setFirstTabActive();
    }

    _bindBackButton() {
        $(".back-to-overview").on("click", (e) => {
            this.navigateBack(e.target);
        });
    }

    /**
     * Scale template iframes based on a max height so that most templates are more consistent
     */
    scaleRatio() {
        const interfaceOffsetSettings = this.fc.getInterfaceOffsetSettings();
        let width = window.innerWidth - (interfaceOffsetSettings.width || 0);
        let height = window.innerHeight - (interfaceOffsetSettings.height || 0);

        const mobile = width < 767;
        const minHeight = 750 - interfaceOffsetSettings.height || 0;
        let cssObject = false;
        let nScaleH = 1;

        if (height < minHeight && !mobile) {
            let vFactor = minHeight / height;
            let newWidth = (width * vFactor * 100) / 100;
            nScaleH = ((height / minHeight) * 100) / 100;

            cssObject = {
                height: `${minHeight}px`,
                width: `${newWidth}px`,
                "transform-origin": "0 0",
                transform: `scale(${nScaleH})`,
            };
        }
        document.documentElement.style.setProperty("--template-scale", `${nScaleH}`);
        document.querySelectorAll('.viewer .spread-container[data-spread-type="1"]').forEach((iframe) => {
            if (cssObject) {
                Object.assign(iframe.style, cssObject);
                iframe.parentNode.style.setProperty("overflow", "hidden");
            } else {
                iframe.removeAttribute("style");
                iframe.parentNode.style.removeProperty("overflow");
            }
        });
    }

    rendeSidePanelByIssueId(issue_id) {
        let spreads = this.dataHelper.filterAndOrderSpreads(issue_id);
        let issue = this.dataHelper.issues[issue_id];

        this.renderSidePanel(spreads, issue);
    }

    pageLoaded(index, spread_id) {
        document.getElementById(`maglr-article-${spread_id}`).querySelector(".spreadloading").classList.add("loaded");
    }

    pageReady(index, event) {
        if (typeof this._classInterface.pageReady == "function") {
            this._classInterface.pageReady(index, event);
        }
    }

    renderSidePanel(spreads, issue) {
        this.dataHelper.setSpreadUrl(spreads);
        const renderSpreads = spreads.filter((spread) => !spread.hidden); // filter hidden spreads
        this._classInterface.renderSpreadNavigationList(renderSpreads, issue);

        if (this.sidemenu.renderSpreadsPanel(renderSpreads, issue)) {
            //bind overview button if spreads panel is enabled
            this.sidemenu.spreadsPanel
                .getPanelElement()
                ?.querySelector(".back-to-overview")
                ?.addEventListener("click", (e) => {
                    this.sidemenu.closeMenu();
                    e.stopPropagation();
                    e.preventDefault();
                    this.navigateBack();
                    return false;
                });
        }

        if (this.storyMode) {
            this.sidemenu.renderSitemap(this.storyMode.getStructure());
        }

        this.bindHistoryPushLinks();

        this.setActive(spreads[0].spread_id);
    }

    shiftScrollToSpread(spread_id, new_spread_id) {
        const control = this.fc.getSpreadController(spread_id);
        if (control.ACVersion) {
            control?.pauseSpread(spread_id);
            const scroll = window.scrollY || window.pageYOffset;

            if (scroll >= 0) {
                this._scrollingList[spread_id] = scroll;
                $(`#maglr-article-${spread_id}`).find(`.spread-container`).css("transform", `translateY(-${scroll}px)`);
                // window.scrollTo({top:newScroll, behavior:'auto'});
                try {
                    window.scrollTo({ top: 0, behavior: "auto" });
                } catch (error) {
                    console.error("MAGLR ERROR: [shiftScrollToSpread]", error);
                }
            }
        } else if (this._scrollingList[new_spread_id] && this.fc.getSpreadController(new_spread_id).ACVersion) {
            // when new spread has scrolling && does have ACVersion then its a pro spread, remove TemplateMode class so the scrollTo function can work.
            this.getInterfaceInstance().setTemplateMode(false);
        }
    }

    shiftScrollFromSpread(spread_id) {
        let scroll = this._scrollingList[spread_id] || 0;

        if (Object.hasOwn(this._scrollingList, spread_id)) {
            try {
                window.scrollTo({ top: scroll, behavior: "auto" });
            } catch (error) {
                console.error("MAGLR ERROR: [shiftScrollFromSpread]", error);
            }
        }

        if (scroll >= 0) {
            $(`#maglr-article-${spread_id} .spread-container[data-spread-type='2']`).css("transform", "");
        }
    }

    removeSpreadScrollListing(spread_id) {
        if (this._scrollingList[spread_id] > 0) {
            delete this._scrollingList[spread_id];
            $(`#maglr-article-${spread_id} .spread-container[data-spread-type='2']`).css("transform", "");
        }
    }

    isInStoryMode() {
        return this.storyMode;
    }

    doLoadNeighbours() {
        return this.storyMode.getBackgroundSpreadId();
    }

    isInTransition() {
        return (
            (this.storyMode && this._inTransition) ||
            (this.InterfaceTransitions && this.InterfaceTransitions.isAnimating)
        );
    }

    setupAutoplay(seconds) {
        this.stopAutoplay(); // stop just in case
        this._autoplayTimer = setInterval(() => {
            this.fc.nextSpread(true);
        }, seconds * 1000);
    }

    stopAutoplay() {
        this._autoplayTimer && clearInterval(this._autoplayTimer);
    }

    updateBodyScrollLock(lock) {
        if (lock) {
            document.querySelector("html").style.setProperty("overflow", "hidden");
        } else {
            document.querySelector("html").style.removeProperty("overflow");
        }
    }
}
